import React from 'react';
import { Box, Typography } from '@mui/material';
import { formatDateISO } from '../../../../utils/dateUtils';
import { totalDaysInYear, yearStartDate } from './timelinePositioning';

// Tooltip + axis-marker helpers split out of SimpleGanttChart (STD-014).
// `buildMonthMarkers` is pure (testable without React); the formatter functions
// return the same JSX the chart rendered inline before the split.

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export interface MonthMarker {
  position: number;
  label: string;
}

// Evenly-ish spaced month abbreviations across one year's axis. Positions are
// the same percentage scale used for the data bars, so markers line up with
// flight/maintenance ticks. Markers outside [0, 100] are dropped.
export function buildMonthMarkers(year: number): MonthMarker[] {
  const startDate = yearStartDate(year);
  const totalDays = totalDaysInYear(year);
  const markers: MonthMarker[] = [];
  for (let month = 0; month < 12; month++) {
    const markerDate = new Date(year, month, 1);
    const daysFromStart = Math.ceil((markerDate.getTime() - startDate.getTime()) / MS_PER_DAY);
    const percentage = (daysFromStart / totalDays) * 100;
    if (percentage >= 0 && percentage <= 100) {
      markers.push({
        position: percentage,
        label: markerDate.toLocaleDateString('en-US', { month: 'short' }),
      });
    }
  }
  return markers;
}

// Tooltip body for a maintenance bar: title-cased type, date range, performer.
export function formatMaintenanceTooltip(record: {
  start_date: string;
  end_date: string;
  maintenance_type: string;
  performed_by_name?: string;
}): React.ReactNode {
  const startDate = formatDateISO(record.start_date);
  const endDate = formatDateISO(record.end_date);
  const dateRange = startDate === endDate ? startDate : `${startDate} - ${endDate}`;
  const performer = record.performed_by_name || 'Not assigned';

  return (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {record.maintenance_type
          ?.replace(/_/g, ' ')
          .replace(/\b\w/g, (l: string) => l.toUpperCase())}
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.25 }}>
        📅 {dateRange}
      </Typography>
      <Typography variant="body2">👤 {performer}</Typography>
    </Box>
  );
}

// Tooltip body for a note marker: subject, action date, click hint.
export function formatNoteTooltip(note: { subject: string }, date: string): React.ReactNode {
  return (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {note.subject}
      </Typography>
      <Typography variant="body2">📅 {formatDateISO(date)}</Typography>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
        Click to view note
      </Typography>
    </Box>
  );
}
